import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { AnimatePresence, motion } from "framer-motion";
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { colors } from "../../theme/colors";
import { buildMapPoolCombatSeries, buildGameAxisTicks } from "./profileRecentMatches";
import { ProfileChartSkeleton } from "./profileSkeletons";

const MotionBox = motion(Box);

const STAT_OPTIONS = [
    { id: "adr", label: "ADR", dataKey: "adr", suffix: "" },
    { id: "kast", label: "KAST%", dataKey: "kast", suffix: "%" },
    { id: "hs", label: "HS%", dataKey: "hsPercent", suffix: "%" },
];

const AXIS_TICK_SX = {
    fontSize: "0.62rem",
    fontWeight: 500,
    color: colors.text.muted,
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
};

function ChartAxisTick({ x, y, payload }) {
    return (
        <g transform={`translate(${x},${y})`}>
            <foreignObject x={-16} y={-7} width={32} height={16}>
                <Typography component="div" sx={{ ...AXIS_TICK_SX, textAlign: "center" }}>
                    {payload.value}
                </Typography>
            </foreignObject>
        </g>
    );
}

function StatFilterButton({ label, isActive, onClick }) {
    return (
        <MotionBox
            component="button"
            type="button"
            onClick={onClick}
            aria-pressed={isActive}
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.97 }}
            sx={{
                position: "relative",
                overflow: "hidden",
                border: "none",
                cursor: "pointer",
                borderRadius: "999px",
                px: "0.85rem",
                py: "0.48rem",
                fontSize: "0.68rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: isActive ? colors.text.primary : "rgba(255,255,255,0.46)",
                background: isActive
                    ? `linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(246,185,76,0.10) 100%)`
                    : "rgba(255,255,255,0.015)",
                borderWidth: "1px",
                borderStyle: "solid",
                borderColor: isActive
                    ? "rgba(246,185,76,0.24)"
                    : "rgba(255,255,255,0.06)",
                boxShadow: isActive
                    ? `0 0 0 1px rgba(246,185,76,0.04), 0 0 18px rgba(246,185,76,0.08)`
                    : `inset 0 1px 0 rgba(255,255,255,0.02)`,
            }}
        >
            <AnimatePresence mode="wait">
                {isActive ? (
                    <MotionBox
                        layoutId="activeMapCombatStatFilter"
                        sx={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: "999px",
                            border: "1px solid rgba(246,185,76,0.14)",
                            pointerEvents: "none",
                        }}
                    />
                ) : null}
            </AnimatePresence>
            <Box component="span" sx={{ position: "relative", zIndex: 2 }}>
                {label}
            </Box>
        </MotionBox>
    );
}

function CombatTrendTooltip({ active, payload, suffix }) {
    if (!active || !payload?.length) {
        return null;
    }

    const point = payload[0];
    const value = point?.value;

    return (
        <Box
            sx={{
                px: "0.75rem",
                py: "0.55rem",
                borderRadius: "8px",
                bgcolor: "rgba(12,12,14,0.96)",
                border: `1px solid ${alpha(colors.primary.main, 0.2)}`,
            }}
        >
            <Typography
                sx={{
                    fontSize: "0.82rem",
                    fontWeight: 800,
                    color: colors.text.primary,
                    fontVariantNumeric: "tabular-nums",
                }}
            >
                {Number.isFinite(value) ? `${value}${suffix}` : "—"}
            </Typography>
        </Box>
    );
}

function getYDomain(values, statId) {
    if (!values.length) {
        return statId === "adr" ? [0, 100] : [0, 100];
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    const pad = Math.max((max - min) * 0.15, statId === "adr" ? 5 : 3);

    if (statId === "adr") {
        return [
            Math.max(0, Math.floor(min - pad)),
            Math.ceil(max + pad),
        ];
    }

    return [
        Math.max(0, Math.floor(min - pad)),
        Math.min(100, Math.ceil(max + pad)),
    ];
}

export default function MapCombatTrendChart({
    mapsStats = {},
    last25MatchData = {},
    limit = null,
    maxMatches = null,
    isLoading = false,
}) {
    const [activeStatId, setActiveStatId] = useState("adr");

    const series = useMemo(
        () => buildMapPoolCombatSeries(last25MatchData, mapsStats, limit, maxMatches),
        [last25MatchData, mapsStats, limit, maxMatches],
    );

    const activeStat =
        STAT_OPTIONS.find((option) => option.id === activeStatId) ||
        STAT_OPTIONS[0];

    const chartPoints = useMemo(
        () =>
            series
                .filter((point) => Number.isFinite(point[activeStat.dataKey]))
                .map((point) => ({
                    match: point.match,
                    value: point[activeStat.dataKey],
                })),
        [series, activeStat.dataKey],
    );

    const yDomain = useMemo(
        () => getYDomain(chartPoints.map((point) => point.value), activeStat.id),
        [chartPoints, activeStat.id],
    );

    const gameAxisTicks = useMemo(
        () => buildGameAxisTicks(chartPoints.length),
        [chartPoints.length],
    );

    if (isLoading) {
        return <ProfileChartSkeleton height="7.5rem" statCount={0} />;
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "0.65rem", minWidth: 0 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0.45rem" }}>
                {STAT_OPTIONS.map((option) => (
                    <StatFilterButton
                        key={option.id}
                        label={option.label}
                        isActive={activeStatId === option.id}
                        onClick={() => setActiveStatId(option.id)}
                    />
                ))}
            </Box>

            {chartPoints.length >= 2 ? (
                <Box sx={{ width: "100%", height: "6.75rem", minWidth: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                            data={chartPoints}
                            margin={{ top: 4, right: 6, left: 0, bottom: 0 }}
                        >
                            <CartesianGrid
                                vertical={false}
                                stroke={alpha(colors.text.muted, 0.08)}
                            />
                            <XAxis
                                dataKey="match"
                                type="number"
                                domain={[1, Math.max(chartPoints.length, 1)]}
                                ticks={gameAxisTicks}
                                tick={<ChartAxisTick />}
                                axisLine={false}
                                tickLine={false}
                                allowDecimals={false}
                            />
                            <YAxis
                                domain={yDomain}
                                tick={<ChartAxisTick />}
                                axisLine={false}
                                tickLine={false}
                                width={32}
                                tickCount={4}
                            />
                            <Tooltip
                                content={
                                    <CombatTrendTooltip suffix={activeStat.suffix} />
                                }
                                cursor={{
                                    stroke: alpha(colors.primary.main, 0.2),
                                    strokeWidth: 1,
                                    strokeDasharray: "4 4",
                                }}
                            />
                            <Line
                                type="linear"
                                dataKey="value"
                                stroke={colors.primary.main}
                                strokeWidth={1.5}
                                dot={false}
                                activeDot={{
                                    r: 4,
                                    fill: colors.primary.main,
                                    stroke: alpha(colors.primary.main, 0.35),
                                    strokeWidth: 2,
                                }}
                                isAnimationActive={false}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            ) : (
                <Box
                    sx={{
                        height: "6.75rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "10px",
                        border: "1px solid rgba(255,255,255,0.05)",
                        bgcolor: "rgba(255,255,255,0.02)",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "0.68rem",
                            fontWeight: 500,
                            color: "rgba(255,255,255,0.38)",
                        }}
                    >
                        Not enough match data
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

MapCombatTrendChart.propTypes = {
    mapsStats: PropTypes.object,
    last25MatchData: PropTypes.object,
    limit: PropTypes.number,
    maxMatches: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
    isLoading: PropTypes.bool,
};
